import React, { Fragment, MouseEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import ScrollWrapper from '../../../shared/common/ScrollWrapper';
import { LOANFORMAT_EAUDIOBOOKS, LOANFORMAT_EBOOKS } from '../../../store/loanFormat/actions';
import GenericDropDown from '../../../shared/common/GenericDropDown';
import { RSFieldValue, RSLoanFormat } from './ReserveSettingsComponents';
import { ReserveCheckBox } from './ReserveCheckBox';
import { ReserveHint } from './ReserveHint';
import { mapCopyFormula, mapMaxCopiesValueToKey } from '../constants';
import { ReserveListDetailOut } from '../../../store/api';
import { Trans, useTranslation } from 'react-i18next';
import { animateHintScroll } from './ReserveUtils';
import isElement from 'lodash/isElement';
import {
    doesElementExceedWindowBottom,
    isFooterOverflownBy,
    setElementHeightBasedOnWindowEnd
} from '../../../shared/utils/utils-clipping';

const SettingsText = styled.span<{ readonly mandatory?: boolean; readonly bold?: boolean }>`
    font-weight: ${props => (props.bold ? 'bold' : 'initial')};
    font-size: ${props => (props.mandatory ? '16px' : '14px')};
`;

const DivInlineFlex = styled.div`
    display: inline-flex;
    align-items: center;
    font-size: 16px;
`;
const SettingsLabel = styled.span`
    font-size: 16px;
    color: #7a7a7a;
`;

const StyledDefaultDD = styled.div<{
    readonly expanded?: boolean;
    readonly hasSingleItem?: boolean;
    readonly isHintShown?: boolean;
}>`
    && {
        && {
            top: 30px;
            min-width: ${props => (props.expanded ? '550px' : 'initial')};
            right: initial !important;
            left: 200px !important;
            width: ${props => (props.hasSingleItem ? (props.isHintShown ? '520px' : 'unset') : '720px')};
        }
    }
`;

const ActiveArrow = styled.span`
    && {
        && {
            left: 100px;
        }
    }
`;

const ReserveDetailSettingsWrapper = styled.div``;
const ReserveDetailSettingsContainer = styled.div<{ readonly isSingleItem: boolean }>`
    display: flex;
    justify-content: ${props => (props.isSingleItem ? 'flex-start' : 'space-around')};
    margin: ${props => (props.isSingleItem ? '20px' : '30px 0')};
`;
export const RDSColumn = styled.div.attrs({ 'data-displayname': 'ReserveDetailDDColumn' })``;

export const RDSMaxCopiesRow = styled.div`
    font-size: 14px;
`;

type Props = {
    readonly reserveReportDetails: ReserveListDetailOut | undefined;
    readonly ebookReserveRatio: number;
    readonly eAudiobookReserveRatio: number;
};

export const ReserveDetailSettings = (props: Props) => {
    const { t } = useTranslation();
    const hintButtonLineRef = useRef(null);
    const isSingleLoanFormat = useRef(false);
    const [isHintShown, setIsHintShown] = useState(false);
    const [calcHeightToPreventOverflows, setCalcHeightToPreventOverflows] = useState('100%');

    useEffect(() => {
        if (!isHintShown) {
            setCalcHeightToPreventOverflows('100%');
        } else {
            const scrollWrapperDiv = document.getElementsByClassName('js-scrollwrapper')?.[0];
            if (!isElement(scrollWrapperDiv)) {
                return;
            }
            const currScrollWrapperHeight = parseFloat(
                window.getComputedStyle(scrollWrapperDiv).getPropertyValue('height')
            );
            const minScrollWrapperHeight = 300;

            if (currScrollWrapperHeight > minScrollWrapperHeight && doesElementExceedWindowBottom(scrollWrapperDiv)) {
                const newHeight = setElementHeightBasedOnWindowEnd(scrollWrapperDiv, minScrollWrapperHeight);
                setCalcHeightToPreventOverflows(`${newHeight}px`);
            } else if (isFooterOverflownBy(scrollWrapperDiv)) {
                setCalcHeightToPreventOverflows('300px');
            }
        }
        if (isHintShown) {
            if (hintButtonLineRef.current) {
                animateHintScroll(hintButtonLineRef.current, '.expanded-dd.active .js-scrollwrapper');
            }
        }
    }, [isHintShown]);

    function toggleHint(event: MouseEvent<HTMLAnchorElement>) {
        event.stopPropagation();
        setIsHintShown(!isHintShown);
    }

    const ebookSettings = props.reserveReportDetails?.loanFormatSettings?.[LOANFORMAT_EBOOKS] ?? {};
    const ebooksIsChecked = !isEmpty(ebookSettings);
    const ebookCopyFormulaKey = ebookSettings?.copyFormula;
    const ebookCopyFormula = mapCopyFormula(ebookCopyFormulaKey);
    const ebookMaxCopies = mapMaxCopiesValueToKey(ebookSettings.maxCopies);
    const ebookReservesPerCopy = ebookSettings.reserveRatio;
    const eaudiobookSettings = props.reserveReportDetails?.loanFormatSettings?.[LOANFORMAT_EAUDIOBOOKS] ?? {};
    const eaudiobooksIsChecked = !isEmpty(eaudiobookSettings);
    const eaudiobookCopyFormulaKey = eaudiobookSettings?.copyFormula;
    const eaudiobookCopyFormula = mapCopyFormula(eaudiobookCopyFormulaKey);
    const eaudiobookMaxCopies = mapMaxCopiesValueToKey(eaudiobookSettings.maxCopies);
    const eaudiobookReservesPerCopy = eaudiobookSettings.reserveRatio;
    isSingleLoanFormat.current = !(ebooksIsChecked && eaudiobooksIsChecked);

    const ReservesPerCopyLimit = () => (
        <>
            {props.ebookReserveRatio > 0 ? (
                <Fragment>
                    <i className="bolinda-icons cold-bgreen" style={{ marginLeft: '5px' }}>
                        ebook
                    </i>
                    <SettingsText mandatory={true} bold={true}>
                        {props.ebookReserveRatio}
                    </SettingsText>
                </Fragment>
            ) : null}
            {props.eAudiobookReserveRatio > 0 ? (
                <Fragment>
                    <i className="bolinda-icons cold-bgreen" style={{ marginLeft: '5px' }}>
                        audiobook
                    </i>
                    <SettingsText mandatory={true} bold={true}>
                        {props.eAudiobookReserveRatio}
                    </SettingsText>
                </Fragment>
            ) : null}
        </>
    );

    const EbookReservesPerCopy = () => (
        <RSFieldValue>
            <span className="selectedValue">{ebookReservesPerCopy}</span>
        </RSFieldValue>
    );

    const EAudioBookReservesPerCopy = () => (
        <RSFieldValue>
            <span className="selectedValue">{eaudiobookReservesPerCopy}</span>
        </RSFieldValue>
    );

    const EbookMaxCopies = () => (
        <RSFieldValue>
            {t(`reserveReports.defaultSettings.maxCopies.${ebookMaxCopies}`, {
                defaultValue: ebookMaxCopies
            })}
        </RSFieldValue>
    );

    const EAudioBookMaxCopies = () => (
        <RSFieldValue>
            {t(`reserveReports.defaultSettings.maxCopies.${eaudiobookMaxCopies}`, {
                defaultValue: eaudiobookMaxCopies
            })}
        </RSFieldValue>
    );

    const EbookFormulaLabel = () => (
        <RSFieldValue>
            {ebookCopyFormula?.value
                ? t(`reserveReports.defaultSettings.copyFormulaKey.${ebookCopyFormula.key.toLowerCase()}`, {
                      defaultValue: ebookCopyFormula.value
                  })
                : null}
        </RSFieldValue>
    );

    const EAudioBookFormulaLabel = () => (
        <RSFieldValue>
            {eaudiobookCopyFormula?.value
                ? t(`reserveReports.defaultSettings.copyFormulaKey.${eaudiobookCopyFormula.key.toLowerCase()}`, {
                      defaultValue: eaudiobookCopyFormula.value
                  })
                : null}
        </RSFieldValue>
    );

    return (
        <GenericDropDown
            className={classNames('default-dd-nav', 'expanded-dd', 'dd-a-r', 'reserve-settings-dd')}
            onCloseListener={() => setIsHintShown(false)}
            dataDisplayname={'ReserveDetailDropdown'}
        >
            <a data-toggle-dropdown="true" className="bt-dd">
                <DivInlineFlex>
                    <i className="bolinda-icons orange" style={{ marginRight: '5px' }}>
                        reserves_per_copy
                    </i>
                    <Trans i18nKey="reserveReports.reservesPerCopyLimit" components={{ 1: <ReservesPerCopyLimit /> }} />
                    <SettingsLabel style={{ marginLeft: '15px' }}>
                        {props.reserveReportDetails?.generatedWithDefaults
                            ? t('reserveReports.reserveReport.viewReport.defaultSettings')
                            : t('reserveReports.reserveReport.viewReport.customSettings')}
                    </SettingsLabel>
                    <i className="material-icons md-up closed">keyboard_arrow_down</i>
                    <i className="material-icons md-up opened">keyboard_arrow_up</i>
                </DivInlineFlex>
            </a>
            <StyledDefaultDD
                hasSingleItem={isSingleLoanFormat.current}
                isHintShown={isHintShown}
                expanded={isHintShown}
                className="default-dd"
            >
                <ActiveArrow className="active-arrow" />
                <ScrollWrapper
                    maxHeight={calcHeightToPreventOverflows}
                    className="js-scrollwrapper"
                    style={{ padding: '10px' }}
                >
                    <ReserveDetailSettingsWrapper>
                        <ReserveDetailSettingsContainer isSingleItem={isSingleLoanFormat.current}>
                            {ebooksIsChecked ? (
                                <RDSColumn>
                                    <RSLoanFormat>
                                        <ReserveCheckBox
                                            isEnabled={false}
                                            isChecked={ebooksIsChecked}
                                            text={t('loanFormat.plain.ebooks', { count: 10 })}
                                        />
                                    </RSLoanFormat>
                                    <RDSMaxCopiesRow>
                                        <Trans
                                            i18nKey="reserveReports.reservesPerCopyLimit"
                                            components={{
                                                1: <EbookReservesPerCopy />
                                            }}
                                        />
                                    </RDSMaxCopiesRow>
                                    <RDSMaxCopiesRow>
                                        <Trans
                                            i18nKey="reserveReports.maxCopiesInCollection"
                                            components={{ 1: <EbookMaxCopies /> }}
                                        />
                                    </RDSMaxCopiesRow>
                                    <RDSMaxCopiesRow>
                                        <Trans
                                            i18nKey="reserveReports.calculationOfAdditionalCopies"
                                            components={{ 1: <EbookFormulaLabel /> }}
                                        />
                                    </RDSMaxCopiesRow>
                                </RDSColumn>
                            ) : null}
                            {eaudiobooksIsChecked ? (
                                <RDSColumn>
                                    <ReserveCheckBox
                                        isEnabled={false}
                                        isChecked={eaudiobooksIsChecked}
                                        text={t('loanFormat.plain.eaudiobooks', { count: 10 })}
                                    />
                                    <RDSMaxCopiesRow>
                                        <Trans
                                            i18nKey="reserveReports.reservesPerCopyLimit"
                                            components={{
                                                1: <EAudioBookReservesPerCopy />
                                            }}
                                        />
                                    </RDSMaxCopiesRow>
                                    <RDSMaxCopiesRow>
                                        <Trans
                                            i18nKey="reserveReports.maxCopiesInCollection"
                                            components={{ 1: <EAudioBookMaxCopies /> }}
                                        />
                                    </RDSMaxCopiesRow>
                                    <RDSMaxCopiesRow>
                                        <Trans
                                            i18nKey="reserveReports.calculationOfAdditionalCopies"
                                            components={{ 1: <EAudioBookFormulaLabel /> }}
                                        />
                                    </RDSMaxCopiesRow>
                                </RDSColumn>
                            ) : null}
                        </ReserveDetailSettingsContainer>

                        <div ref={hintButtonLineRef} />
                        <ReserveHint
                            marginTop="25px"
                            marginLeft={isSingleLoanFormat.current ? '20px' : '40px'}
                            toggleHint={toggleHint}
                            isHintShown={isHintShown}
                            borderOverflow={isSingleLoanFormat.current ? '30px' : '50px'}
                            lineHeight="16px"
                            marginLeftRight={isSingleLoanFormat.current ? '20px' : '40px'}
                        />
                    </ReserveDetailSettingsWrapper>
                </ScrollWrapper>
            </StyledDefaultDD>
        </GenericDropDown>
    );
};
